import React, {useState, useEffect, useRef} from 'react';
import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {FemaGoTable} from 'app-ui-componentlibrary';
import {
  Segment,
  Table,
  Header,
  Loader,
  Message,
  Pagination,
  Grid,
  Button,
  Input,
  Icon,
} from 'semantic-ui-react';
import _ from 'lodash';
import {compileContent, evaluateOperator} from 'common/utils';
import {FemaGoModal} from 'app-ui-componentlibrary';
import {ContentDispatch} from 'framework';

const MODALS = {
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  NONE: 'none',
  DELETE: 'delete',
};

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const ExpandableTable = (props) => {
  const {
    entityDataPath,
    entityType,
    saveEntityData,
    validationId,
    modalTitle,
    modalAction,
    addRefTitle,
    emptyListMessage,
    modalSize,
    statusValidationIndex,
    columns,
    childColumns,
    header,
    headerSize,
    childHeader,
    pageSize,
    filterByInput,
    filterPath,
    validationErrors,
    chevron,
    searchBarActive,
    hideChildRowRules,
  } = props;

  const data = _.isArray(props.data) || _.isEmpty(props.data) ? props.data : [props.data];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openModal, setOpenModal] = useState(MODALS.NONE);
  const [showModal, setShowModal] = useState(MODALS.NONE);
  const [loading, setLoading] = useState(false);
  const [index, setIndex] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableHeight, setTableHeight] = useState('63px');
  const [currentPage, setCurrentPage] = useState(0);
  const [requestQueue, setRequestQueue] = useState([]);
  const [dataToRead, setDataToRead] = useState(data);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [childTableOpen, setChildTableOpen] = useState(false);
  const [dataIndex, setDataIndex] = useState(null);
  const requestQueueRef = useRef();
  requestQueueRef.current = requestQueue;
  const tableRef = useRef(null);
  const prevSearchTerm = usePrevious(searchTerm);

  const evaluateHideChildRowRules = (row) => {
    let result = false;
    _.forEach(hideChildRowRules, (rule) => {
      const data = compileContent(rule.data, {row, ...props});
      result = evaluateOperator(rule.operator, data, rule.value);
      if (result) return false;
    });
    return result;
  };

  const dataWithHideRules = _.map(dataToRead, (data) => {
    return {
      ...data,
      hideChildRow: evaluateHideChildRowRules(data),
    };
  });

  const dataToRender = !_.isUndefined(pageSize)
    ? _.slice(dataWithHideRules, (activePage - 1) * pageSize, activePage * pageSize)
    : _.slice(
        dataWithHideRules,
        (activePage - 1) * _.size(data),
        activePage * _.size(data)
      );

  const saveAction = () => {
    const {modalItem} = props;

    let result = [];
    _.forEach(data, (row) => {
      result.push(row);
    });

    if (openModal === MODALS.CREATE) {
      result.push(modalItem);
    } else if (openModal === MODALS.EDIT) {
      result.splice(index, 1);
      result.push(modalItem);
    }
    saveEntityData(entityDataPath, entityType, result);
    setDataToRead(result);
    clearEntityDataPath();
  };

  const confirmAction = () => {
    saveAction();
    setIsModalOpen(false);
    setOpenModal(MODALS.NONE);
    setShowModal(MODALS.NONE);
  };

  const cancelAction = () => {
    setIsModalOpen(false);
    setOpenModal(MODALS.NONE);
    setShowModal(MODALS.NONE);
    clearEntityDataPath();
  };

  const clearEntityDataPath = () => {
    props.clearEntityData('context.reportActionModal', false, validationId);
  };

  const renderAddEditModal = () => {
    const validModalEntry = _.reduce(
      validationErrors,
      (acc, err) => acc && err.isValid,
      true
    );

    const modalContents = compileContent(props.modalContents);

    return (
      <FemaGoModal
        title={showModal + ' ' + modalTitle}
        open={isModalOpen}
        buttonText={showModal + ' ' + modalAction}
        disableButton={!validModalEntry}
        confirmAction={confirmAction}
        cancelAction={cancelAction}
        clearEntityData={clearEntityDataPath}
        size={modalSize ? modalSize : 'tiny'}
        readOnly={props.readOnly}
      >
        <span>
          <ContentDispatch config={modalContents} />
        </span>
      </FemaGoModal>
    );
  };

  const handleAddModal = () => {
    setIsModalOpen(!isModalOpen);
    setOpenModal(MODALS.CREATE);
    setShowModal('Add');

    saveEntityData('context.reportActionModal', entityType, {});
  };

  const handleEditModal = (index, data) => {
    setIsModalOpen(!isModalOpen);
    setOpenModal(MODALS.EDIT);
    setShowModal(props.readOnly ? 'View' : 'Edit');
    setIndex(index);

    saveEntityData('context.reportActionModal', entityType, data);
  };

  const handleDelete = (index) => {
    let newData = _.cloneDeep(data);
    newData.splice(index, 1);
    saveEntityData(entityDataPath, entityType, newData);
    setDataToRead(newData);
  };

  const handleTermChange = (e, entry) => {
    setSearchTerm(entry.value);
    setCurrentPage(0);
  };

  const handleExpandRow = (index, hideChildRow) => {
    if (!hideChildRow) {
      index === dataIndex && childTableOpen
        ? setChildTableOpen(!childTableOpen)
        : setChildTableOpen(true);
      setDataIndex(index);
    }
  };

  const expandableChildRow = (data) => {
    const childTableKeys = _.keys(childColumns);
    const dataEntry = data[dataIndex];
    const tableCell = (child, key) => (
      <FemaGoTable.TextCell key={key} width={3}>
        {child}
      </FemaGoTable.TextCell>
    );

    return (
      <>
        <Header as={'h5'} content={childHeader} />
        <FemaGoTable
          columns={_.size(childColumns)}
          withForm={false}
          summary={childHeader}
        >
          <FemaGoTable.Header>
            {_.map(childTableKeys, (k) => (
              <FemaGoTable.HeaderCell key={k + ' header'}>{k}</FemaGoTable.HeaderCell>
            ))}
          </FemaGoTable.Header>
          <FemaGoTable.Body>
            <FemaGoTable.Row>
              {_.map(childTableKeys, (k) => {
                const curr = childColumns[k];
                return tableCell(compileContent(curr, {dataEntry, ...props}, true), k);
              })}
            </FemaGoTable.Row>
          </FemaGoTable.Body>
        </FemaGoTable>
      </>
    );
  };

  const onSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    getContents(activePage);
  };

  const getContents = (activePage) => {
    setLoading(true);
    setRequestQueue([
      ...requestQueue,
      {
        type: 'getContentsAction',
        payload: {
          activePage,
        },
      },
    ]);
  };

  useEffect(() => {
    if (requestQueue.length && !requestQueue[0].loading) {
      const action = {
        ...requestQueue[0],
      };
      action.loading = true;
      setRequestQueue([action, ...requestQueue.slice(1)]);
      const cb = () => {
        setRequestQueue(requestQueueRef.current.slice(1));
      };
      switch (action.type) {
        case 'getContentsAction':
          getContentsAction(action.payload.activePage, cb, cb);
          break;
      }
    }
  }, [requestQueue]);

  useEffect(() => {
    if (!isInitialLoad) getContents(activePage);
  }, [currentPage, activePage]);

  useEffect(() => {
    if (prevSearchTerm !== searchTerm && !isInitialLoad) {
      const timer = setTimeout(() => {
        getContents(activePage);
      }, 1200);

      return () => clearTimeout(timer);
    }
  }, [prevSearchTerm, searchTerm]);

  const getContentsAction = (activePage, cb, errorcb) => {
    setLoading(true);
    const term = searchTerm;
    let results = [];
    let curr;
    let entry;
    const keys = _.keys(columns);

    if (_.get(tableRef, 'current')) {
      let height = tableRef.current.getBoundingClientRect().height;
      setTableHeight(height);
    }

    _.forEach(data, (row) => {
      let verified = false;

      _.forEach(keys, (k, j) => {
        curr = columns[k];
        entry = compileContent(curr, {row, ...props}, true);
        if (_.includes(entry[0], term)) {
          verified = true;
        }
      });

      if (verified) {
        results.push(row);
      }
    });

    Promise.all(results)
      .then(() => {
        let newData = _.cloneDeep(results);
        setDataToRead(newData);
        setLoading(false);
        if (isInitialLoad) setIsInitialLoad(false);
        cb();
      })
      .catch((e) => {
        errorcb();
      });
  };

  const filterEntities = (entities, filterByInput, filterPath) => {
    if (!_.isEmpty(filterByInput) && filterPath !== undefined) {
      const filteredEntities = _.filter(entities, (entity) => {
        let parentEntityId = _.get(entity, filterPath);
        return _.isEqual(parentEntityId, filterByInput);
      });
      return filteredEntities;
    } else {
      return entities;
    }
  };

  const renderEntityActions = ({row, index, actions}) => {
    let actionArray = [];
    for (let i = 0; i < actions.length; i++) {
      actionArray.push(actions[i].key);
    }
    return (
      <FemaGoTable.TextCell textAlign="left">
        {_.includes(actionArray, 'edit') && (
          <a href="#" title="Edit" onClick={() => handleEditModal(index, row)}>
            Edit
          </a>
        )}
        &nbsp;&nbsp;&nbsp;{' '}
        {/* spacing so the text is not directly back to back, should be added between any future action options */}
        {_.includes(actionArray, 'delete') && (
          <a href="#" title="Remove" onClick={() => handleDelete(index)}>
            Remove
          </a>
        )}
      </FemaGoTable.TextCell>
    );
  };

  const renderTable = (data) => {
    const keys = _.keys(columns);
    const tableCell = (child, key) => (
      <FemaGoTable.TextCell key={key}>{child}</FemaGoTable.TextCell>
    );
    return (
      <>
        {searchBarActive ? (
          <div className="search-bar">
            <form id="search-form" onSubmit={onSearch}>
              <span>
                <Input>
                  <Input
                    aria-label={`Search by term`}
                    title={`Search by term`}
                    value={searchTerm}
                    onChange={handleTermChange}
                    onFocus={(e) => e.target.select()}
                    style={{width: '380px'}}
                  />
                  <Button primary icon aria-label="Search Button" title="Search button">
                    <Icon name="search" fitted />
                  </Button>
                </Input>
              </span>
            </form>
          </div>
        ) : (
          ''
        )}
        {loading ? (
          <div
            className="margin-top"
            style={{minHeight: tableHeight, display: 'flex', alignItems: 'center'}}
          >
            <Loader inline="centered" size="large" active>
              Loading...
            </Loader>
          </div>
        ) : (
          <>
            <React.Fragment>
              <FemaGoTable columns={_.size(keys)} withForm={false} summary={header}>
                <FemaGoTable.Header>
                  <FemaGoTable.Row key="entityListHeaderTable">
                    {chevron == 'left' ||
                      (!chevron && (
                        <FemaGoTable.HeaderCell key="chevron"></FemaGoTable.HeaderCell>
                      ))}
                    {!_.isUndefined(statusValidationIndex) && (
                      <FemaGoTable.HeaderCell key="status">Status</FemaGoTable.HeaderCell>
                    )}
                    {_.map(keys, (k) => (
                      <FemaGoTable.HeaderCell
                        key={k + ' header'}
                        width={k != 'Actions' ? 3 : ''}
                      >
                        {k}
                      </FemaGoTable.HeaderCell>
                    ))}
                    {chevron == 'right' && (
                      <FemaGoTable.HeaderCell key="chevron"></FemaGoTable.HeaderCell>
                    )}
                  </FemaGoTable.Row>
                </FemaGoTable.Header>
                {!_.isEmpty(data) ? (
                  <FemaGoTable.Body>
                    {_.map(data, (row, i) => (
                      <>
                        <Table.Row
                          key={i}
                          onClick={() => handleExpandRow(i, row.hideChildRow)}
                          className={
                            childTableOpen && dataIndex === i
                              ? 'expandedTable'
                              : 'unexpandedTable'
                          }
                        >
                          {!row.hideChildRow ? (
                            (chevron == 'left' || !chevron) &&
                            (childTableOpen && dataIndex === i ? (
                              <FemaGoTable.TextCell width={1}>
                                <Icon size="small" name={`chevron up`} color="darkCyan" />
                              </FemaGoTable.TextCell>
                            ) : (
                              <FemaGoTable.TextCell width={1}>
                                <Icon
                                  size="small"
                                  name={`chevron down`}
                                  color="darkCyan"
                                />
                              </FemaGoTable.TextCell>
                            ))
                          ) : (
                            <FemaGoTable.TextCell width={1} />
                          )}
                          {!_.isUndefined(statusValidationIndex) && (
                            <FemaGoTable.StatusCell
                              hasErrors={
                                props.readOnly
                                  ? false
                                  : _.get(
                                      validationErrors,
                                      `${statusValidationIndex}.isValid`,
                                      false
                                    )
                                  ? false
                                  : !_.get(
                                      validationErrors,
                                      `${statusValidationIndex}.${_.findIndex(data, (d) =>
                                        _.isEqual(d, row)
                                      )}.isValid`,
                                      false
                                    )
                              }
                              width={1}
                              float={'left'}
                              cellID={`Row ${i + 1}`}
                            />
                          )}
                          {_.map(keys, (k, j) => {
                            const curr = columns[k];
                            switch (k) {
                              case 'Actions':
                                let actionProps = _.transform(
                                  curr.props,
                                  (p, v, k) => (p[k] = compileContent(v, {row, ...props}))
                                );
                                return tableCell(
                                  renderEntityActions({
                                    row,
                                    index: `${i}`,
                                    actions: curr.actions,
                                    ...actionProps,
                                    ...props,
                                  }),
                                  j
                                );
                              default:
                                return tableCell(
                                  compileContent(curr, {row, ...props}, true),
                                  j
                                );
                            }
                          })}
                          {chevron == 'right' &&
                            (childTableOpen && dataIndex === i ? (
                              <FemaGoTable.TextCell width={1}>
                                <Icon size="small" name={`chevron up`} color="darkCyan" />
                              </FemaGoTable.TextCell>
                            ) : (
                              <FemaGoTable.TextCell width={1}>
                                <Icon
                                  size="small"
                                  name={`chevron down`}
                                  color="darkCyan"
                                />
                              </FemaGoTable.TextCell>
                            ))}
                        </Table.Row>
                        {!row.hideChildRow && childTableOpen && dataIndex == i && (
                          <Table.Row className="childRow">
                            <FemaGoTable.TextCell colSpan={_.size(keys)}>
                              {expandableChildRow(data)}
                            </FemaGoTable.TextCell>
                          </Table.Row>
                        )}
                      </>
                    ))}
                  </FemaGoTable.Body>
                ) : (
                  <FemaGoTable.Body>
                    <FemaGoTable.TextCell>
                      {<div className={`errorMessages`}>{emptyListMessage}</div>}
                    </FemaGoTable.TextCell>
                  </FemaGoTable.Body>
                )}
              </FemaGoTable>
            </React.Fragment>
          </>
        )}
      </>
    );
  };

  const dataToDisplay = filterEntities(dataToRender, filterByInput, filterPath);

  return (
    <Segment
      className={props.bordered ? 'expandableTableBordered' : 'expandableTableBorderless'}
    >
      <Header as={headerSize || 'h4'} content={header} id={header}>
        {!props.readOnly ? (
          <Grid.Column width={10}>
            {header}
            <a
              href="#"
              title={addRefTitle}
              onClick={handleAddModal}
              className="boldExpandableTable"
            >
              + {addRefTitle}{' '}
            </a>
          </Grid.Column>
        ) : (
          header
        )}
      </Header>
      {renderTable(dataToDisplay)}
      {renderAddEditModal()}
      {_.size(data) > pageSize ? (
        <div className="pagination-wrapper leftAlign">
          <Pagination
            totalPages={Math.ceil(_.size(data) / pageSize)}
            activePage={activePage}
            onPageChange={(e, data) => setActivePage(data.activePage)}
          />
        </div>
      ) : (
        ''
      )}
    </Segment>
  );
};

export default connectDynamic(ExpandableTable);
